import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { can, type Permission, type Role, type SessionUser } from '@khadi/shared';
import { api, ApiError } from './api';

/**
 * Angemeldetes Konto und dessen Rechte.
 *
 * Die Oberfläche blendet nur aus, was die Rolle ohnehin nicht darf —
 * die verbindliche Prüfung sitzt in der API.
 */

interface AuthValue {
  user: SessionUser | null;
  isLoading: boolean;
  can: (permission: Permission) => boolean;
  role: Role | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        return await api<SessionUser>('/auth/me');
      } catch (error) {
        // 401 ist hier kein Fehler, sondern „nicht angemeldet".
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  const value = useMemo<AuthValue>(() => {
    const user = data ?? null;
    return {
      user,
      isLoading,
      role: user?.role ?? null,
      can: (permission) => (user ? can(user.role, permission) : false),
      refresh: async () => {
        await queryClient.invalidateQueries({ queryKey: ['session'] });
      },
      logout: async () => {
        await api('/auth/logout', { method: 'POST' });
        queryClient.clear();
        window.location.href = '/anmelden';
      },
    };
  }, [data, isLoading, queryClient]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth benötigt einen AuthProvider');
  return context;
}
